import { supabase } from '../supabase';
import type { Snap, PaginatedResponse } from '@/types/database.types';

/**
 * Get SNAPs for the feed that the user hasn't voted on yet
 * @param page Page number (starting from 1)
 * @param limit Number of items per page
 * @returns Paginated response with SNAPs
 */
export async function getFeedSnaps(
  page = 1,
  limit = 10
): Promise<PaginatedResponse<Snap>> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: [], total: 0, page, limit };
    }

    // Get user's votes to exclude SNAPs they've already voted on
    const { data: userVotes } = await supabase
      .from('votes')
      .select('snap_id')
      .eq('user_id', user.id);

    const votedSnapIds = userVotes?.map(vote => vote.snap_id) || [];

    // Get user profile to check age verification status
    const { data: profile } = await supabase
      .from('profiles')
      .select('age_verified')
      .eq('user_id', user.id)
      .single();

    const isAgeVerified = profile?.age_verified || false;

    // Query for SNAPs the user hasn't voted on yet
    let query = supabase
      .from('snaps')
      .select(`
        id,
        title,
        total_votes,
        view_count,
        likes_count,
        created_at,
        ends_at,
        is_system,
        cashsnap,
        creator_id,
        description,
        is_anonymous,
        creator:user_profiles!inner(
          id,
          username,
          avatar_url,
          created_at,
          followers_count
        ),
        options:snap_options!snap_options_snap_id_fkey(
          id,
          title,
          description,
          image_url,
          vote_count
        ),
        comments:comments(count),
        categories:snap_categories(category)
      `, { count: 'exact' })
      .eq('is_private', false) // Only public SNAPs

    // Exclude CashSNAPs if user is not age verified
    if (!isAgeVerified) {
      query = query.eq('cashsnap', false);
    }
      
    query = query.order('created_at', { ascending: false });

    // Exclude SNAPs the user has already voted on
    if (votedSnapIds.length > 0) {
      query = query.not('id', 'in', `(${votedSnapIds.join(',')})`);
    }
    
    // Exclude SNAPs created by the user
    query = query.neq('creator_id', user.id);

    // Get paginated data
    const { data: snapsData, error: snapsError, count } = await query
      .range((page - 1) * limit, page * limit - 1);

    if (snapsError) {
      console.error('Error fetching feed snaps:', snapsError);
      return { data: [], total: 0, page, limit };
    }

    if (!snapsData) {
      return { data: [], total: 0, page, limit };
    }

    // Get user's likes
    const { data: likes } = await supabase
      .from('snap_likes')
      .select('snap_id')
      .eq('user_id', user.id);
    
    const likedSnapIds = new Set(likes?.map(like => like.snap_id) || []);

    const formattedData = snapsData.map(snap => {
      return {
        ...snap,
        categories: snap.categories?.map((c: any) => c.category) || [],
        isOwner: user?.id === snap.creator_id,
        commentCount: snap.comments?.[0]?.count || 0,
        userLiked: likedSnapIds.has(snap.id),
        userVote: null // User hasn't voted on these SNAPs yet
      };
    });

    return {
      data: formattedData,
      total: count || 0,
      page,
      limit
    };
  } catch (error) {
    console.error('Error in getFeedSnaps:', error);
    return { data: [], total: 0, page, limit };
  }
}

/**
 * Get SNAPs the user has already voted on
 * @param page Page number (starting from 1)
 * @param limit Number of items per page
 * @returns Paginated response with SNAPs
 */
export async function getVotedSnaps(
  page = 1,
  limit = 10
): Promise<PaginatedResponse<Snap>> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: [], total: 0, page, limit };
    }

    // Get user profile to check age verification status
    const { data: profile } = await supabase
      .from('profiles')
      .select('age_verified')
      .eq('user_id', user.id)
      .single();

    const isAgeVerified = profile?.age_verified || false;

    // Get user's votes with pagination
    let votesQuery = supabase
      .from('votes')
      .select('snap_id, option_id', { count: 'exact' })
      .eq('user_id', user.id);

    // If user is not age verified, exclude votes for CashSNAPs
    if (!isAgeVerified) {
      votesQuery = votesQuery.not(
        'snap_id', 
        'in', 
        supabase
          .from('snaps')
          .select('id')
          .eq('cashsnap', true)
          .then(({ data }) => data?.map(s => s.id) || [])
      );
    }

    const { data: userVotes, count: votesCount, error: votesError } = await votesQuery
      .order('created_at', { ascending: false })
      .range((page - 1) * limit, page * limit - 1);

    if (votesError) {
      console.error('Error fetching user votes:', votesError);
      return { data: [], total: 0, page, limit };
    }

    if (!userVotes || userVotes.length === 0) {
      return { data: [], total: 0, page, limit };
    }

    const votedSnapIds = userVotes.map(vote => vote.snap_id);
    const userVotesMap = new Map(userVotes.map(vote => [vote.snap_id, vote.option_id]));

    // Get the SNAPs the user has voted on
    let snapsQuery = supabase
      .from('snaps')
      .select(`
        id,
        title,
        total_votes,
        view_count,
        likes_count,
        created_at,
        ends_at,
        is_system,
        cashsnap,
        creator_id,
        description,
        is_anonymous,
        creator:user_profiles!inner(
          id,
          username,
          avatar_url,
          created_at,
          followers_count
        ),
        options:snap_options!snap_options_snap_id_fkey(
          id,
          title,
          description,
          image_url,
          vote_count
        ),
        comments:comments(count),
        categories:snap_categories(category)
      `)
      .in('id', votedSnapIds);

    // If user is not age verified, exclude CashSNAPs
    if (!isAgeVerified) {
      snapsQuery = snapsQuery.eq('cashsnap', false);
    }

    const { data: snapsData, error: snapsError } = await snapsQuery;

    if (snapsError) {
      console.error('Error fetching voted snaps:', snapsError);
      return { data: [], total: 0, page, limit };
    }

    if (!snapsData) {
      return { data: [], total: 0, page, limit };
    }

    // Get user's likes
    const { data: likes } = await supabase
      .from('snap_likes')
      .select('snap_id')
      .eq('user_id', user.id);
    
    const likedSnapIds = new Set(likes?.map(like => like.snap_id) || []);

    // Sort the snaps to match the order of votes
    const sortedSnaps = votedSnapIds
      .map(snapId => snapsData.find(snap => snap.id === snapId))
      .filter(Boolean) as Snap[];

    const formattedData = sortedSnaps.map(snap => {
      return {
        ...snap,
        categories: snap.categories?.map((c: any) => c.category) || [],
        isOwner: user?.id === snap.creator_id,
        commentCount: snap.comments?.[0]?.count || 0,
        userLiked: likedSnapIds.has(snap.id),
        userVote: userVotesMap.get(snap.id) || null
      };
    });

    return {
      data: formattedData,
      total: votesCount || 0,
      page,
      limit
    };
  } catch (error) {
    console.error('Error in getVotedSnaps:', error);
    return { data: [], total: 0, page, limit };
  }
}